import * as React from "react";
import {Link as RouterLink, useParams, useSearchParams} from "react-router-dom";
import {Alert, Box, Link, Typography} from "@mui/material";
import Loading from "../../components/loading";
import {campusProgramCellUpsert, getCampusPrograms} from "./programSetService";
import moment from "moment";

type CellKey = string;

function cellKey(programId: number, eduLevelId: number): CellKey {
    return `${programId}:${eduLevelId}`;
}

function applyCellLook(el: HTMLInputElement, flashing: boolean) {
    if (flashing) {
        el.style.backgroundColor = "#c8e6c9";
        return;
    }
    el.style.backgroundColor = el.value.trim().toLowerCase() === "x" ? "#eada42" : "transparent";
}

type GridProps = {
    programSetId: number;
    campusName: string;
    eduLevels: {id: number, name: string}[];
    programs: {id: number, label: string, subjectId: number, subject: string}[];
    initialValues: Record<CellKey, string>;
    onSaving: (pendingDelta: number) => void;
    onSaved: () => void;
    onError: (msg: string) => void;
};

const ProgramGrid = React.memo(function ProgramGrid(props: GridProps) {
    const {
        programSetId, campusName, eduLevels, programs, initialValues,
        onSaving, onSaved, onError,
    } = props;

    const valuesRef = React.useRef<Record<CellKey, string>>({...initialValues});
    const savedValuesRef = React.useRef<Record<CellKey, string>>({...initialValues});
    const debounceTimers = React.useRef<Record<CellKey, any>>({});
    const saveQueue = React.useRef<Promise<void>>(Promise.resolve());
    const inputRefs = React.useRef<Record<CellKey, HTMLInputElement | null>>({});

    React.useEffect(() => {
        valuesRef.current = {...initialValues};
        savedValuesRef.current = {...initialValues};
        Object.keys(inputRefs.current).forEach((key) => {
            const el = inputRefs.current[key];
            if (el) {
                el.value = initialValues[key] || "";
                applyCellLook(el, false);
            }
        });
    }, [initialValues]);

    const flashCell = (key: CellKey) => {
        const el = inputRefs.current[key];
        if (!el) return;
        applyCellLook(el, true);
        window.setTimeout(() => {
            const current = inputRefs.current[key];
            if (current) applyCellLook(current, false);
        }, 700);
    };

    const enqueueSave = (programId: number, eduLevelId: number, value: string) => {
        const key = cellKey(programId, eduLevelId);
        const trimmed = value.trim();
        if (trimmed === (savedValuesRef.current[key] || "").trim()) {
            return;
        }

        onSaving(1);

        saveQueue.current = saveQueue.current.then(async () => {
            const current = (valuesRef.current[key] || "").trim();
            if (current !== trimmed || trimmed === (savedValuesRef.current[key] || "").trim()) {
                onSaving(-1);
                return;
            }

            const result = await campusProgramCellUpsert(programSetId, campusName, programId, eduLevelId, trimmed);
            onSaving(-1);

            if (!result) {
                onError("Save failed");
                return;
            }

            savedValuesRef.current = {...savedValuesRef.current, [key]: result.value || ""};
            flashCell(key);
            onSaved();
        }).catch(() => {
            onSaving(-1);
            onError("Save failed");
        });
    };

    const scheduleSave = (programId: number, eduLevelId: number, value: string, immediate: boolean) => {
        const key = cellKey(programId, eduLevelId);
        if (debounceTimers.current[key]) {
            clearTimeout(debounceTimers.current[key]);
        }
        if (immediate) {
            enqueueSave(programId, eduLevelId, value);
            return;
        }
        debounceTimers.current[key] = setTimeout(() => {
            enqueueSave(programId, eduLevelId, value);
        }, 400);
    };

    const handleInput = (programId: number, eduLevelId: number, el: HTMLInputElement) => {
        const key = cellKey(programId, eduLevelId);
        valuesRef.current[key] = el.value;
        applyCellLook(el, false);
        scheduleSave(programId, eduLevelId, el.value, false);
    };

    const handlePaste = (
        e: React.ClipboardEvent<HTMLInputElement>,
        startProgramIdx: number,
        startEduIdx: number
    ) => {
        const text = e.clipboardData.getData("text");
        if (!text) return;

        const rows = text.replace(/\r/g, "").split("\n").filter((r, i, arr) => !(i === arr.length - 1 && r === ""));
        const isSingle = rows.length === 1 && !rows[0].includes("\t");
        e.preventDefault();

        if (isSingle) {
            const programId = programs[startProgramIdx].id;
            const eduLevelId = eduLevels[startEduIdx].id;
            const key = cellKey(programId, eduLevelId);
            const el = inputRefs.current[key];
            if (el) {
                el.value = rows[0];
                valuesRef.current[key] = rows[0];
                applyCellLook(el, false);
            }
            scheduleSave(programId, eduLevelId, rows[0], true);
            return;
        }

        const matrix = rows.map((r) => r.split("\t"));
        for (let r = 0; r < matrix.length; r++) {
            const programIdx = startProgramIdx + r;
            if (programIdx >= programs.length) break;
            for (let c = 0; c < matrix[r].length; c++) {
                const eduIdx = startEduIdx + c;
                if (eduIdx >= eduLevels.length) break;
                const programId = programs[programIdx].id;
                const eduLevelId = eduLevels[eduIdx].id;
                const value = matrix[r][c] ?? "";
                const key = cellKey(programId, eduLevelId);
                const el = inputRefs.current[key];
                if (el) {
                    el.value = value;
                    applyCellLook(el, false);
                }
                valuesRef.current[key] = value;
                scheduleSave(programId, eduLevelId, value, true);
            }
        }
    };

    return (
        <div className="psp-grid-wrap">
            <style>{`
                .psp-grid-wrap {
                    max-height: calc(100vh - 220px);
                    width: 100%;
                    overflow: auto;
                    border: 1px solid #ddd;
                    background: #fff;
                }
                .psp-grid {
                    border-collapse: separate;
                    border-spacing: 0;
                    width: 100%;
                    min-width: 100%;
                    table-layout: fixed;
                    font-size: 12px;
                }
                .psp-grid th, .psp-grid td {
                    border-bottom: 1px solid #e0e0e0;
                    border-right: 1px solid #eee;
                    padding: 0;
                }
                .psp-grid thead th {
                    position: sticky;
                    top: 0;
                    z-index: 2;
                    background: #f5f5f5;
                    font-weight: 700;
                    padding: 6px 8px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .psp-grid th.psp-sticky, .psp-grid td.psp-sticky {
                    position: sticky;
                    left: 0;
                    z-index: 1;
                    width: 320px;
                    min-width: 320px;
                    max-width: 320px;
                    padding: 4px 8px;
                    white-space: normal;
                    overflow: visible;
                    word-break: break-word;
                    border-right: 2px solid #9e9e9e;
                    box-shadow: 2px 0 0 rgba(0, 0, 0, 0.08);
                    line-height: 1.25;
                    background: #fff;
                }
                .psp-grid thead th.psp-sticky {
                    z-index: 3;
                }
                .psp-grid tbody tr:focus-within td.psp-sticky {
                    background: #bbdefb !important;
                    font-weight: 700;
                    box-shadow: inset 3px 0 0 #1976d2, 2px 0 0 rgba(0, 0, 0, 0.08);
                }
                .psp-grid input {
                    width: 100%;
                    box-sizing: border-box;
                    border: none;
                    outline: none;
                    padding: 4px 6px;
                    font-size: 12px;
                    background: transparent;
                }
                .psp-grid input:focus {
                    outline: 1px solid #1976d2;
                    outline-offset: -1px;
                }
                .psp-grid tr.psp-subject-start > td {
                    border-top: 2px solid #9e9e9e;
                }
                .psp-grid tr.psp-subject-header > td {
                    background: #e8e8e8 !important;
                    font-weight: 700;
                    font-size: 12px;
                    padding: 6px 8px;
                    border-top: 2px solid #9e9e9e;
                    border-bottom: 1px solid #bdbdbd;
                    border-right: none;
                    box-shadow: none;
                    position: static;
                }
            `}</style>
            <table className="psp-grid">
                <thead>
                <tr>
                    <th className="psp-sticky">Program</th>
                    {eduLevels.map((level) => (
                        <th key={level.id}>{level.name}</th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {programs.map((program, programIdx) => {
                    const isSubjectStart = programIdx === 0
                        || programs[programIdx - 1].subjectId !== program.subjectId;
                    const colSpan = eduLevels.length + 1;
                    return (
                        <React.Fragment key={program.id}>
                            {isSubjectStart && (
                                <tr className="psp-subject-header">
                                    <td colSpan={colSpan}>
                                        {program.subject}
                                    </td>
                                </tr>
                            )}
                            <tr>
                                <td className="psp-sticky" title={program.label}>
                                    {program.label}
                                </td>
                                {eduLevels.map((level, eduIdx) => {
                                    const key = cellKey(program.id, level.id);
                                    const value = initialValues[key] || "";
                                    return (
                                        <td key={level.id}>
                                            <input
                                                ref={(el) => { inputRefs.current[key] = el; }}
                                                defaultValue={value}
                                                onChange={(e) => handleInput(program.id, level.id, e.target)}
                                                onBlur={(e) => scheduleSave(program.id, level.id, e.target.value, true)}
                                                onPaste={(e) => handlePaste(e, programIdx, eduIdx)}
                                                onFocus={(e) => e.target.select()}
                                                style={{
                                                    backgroundColor: value.trim().toLowerCase() === "x" ? "#eada42" : "transparent",
                                                }}
                                            />
                                        </td>
                                    );
                                })}
                            </tr>
                        </React.Fragment>
                    );
                })}
                </tbody>
            </table>
        </div>
    );
});

export default function ProgramSetProgramsPage() {
    const {id: idParam} = useParams();
    const [searchParams] = useSearchParams();
    const programSetId = parseInt(idParam || "0", 10);
    const campusName = searchParams.get("name") || "";

    const [loading, setLoading] = React.useState(true);
    const [setName, setSetName] = React.useState("");
    const [eduLevels, setEduLevels] = React.useState([] as any[]);
    const [programs, setPrograms] = React.useState([] as any[]);
    const [initialValues, setInitialValues] = React.useState<Record<CellKey, string>>({});
    const [status, setStatus] = React.useState<"idle" | "saving" | "saved" | "error">("idle");
    const [savedAt, setSavedAt] = React.useState<string>("");
    const [errorMessage, setErrorMessage] = React.useState("");
    const [pendingCount, setPendingCount] = React.useState(0);

    React.useEffect(() => {
        document.title = `Programs | ${campusName || "Campus"} | DN`;
    }, [campusName]);

    React.useEffect(() => {
        const load = async () => {
            if (!programSetId || !campusName) {
                setLoading(false);
                return;
            }
            setLoading(true);
            const data = await getCampusPrograms(programSetId, campusName);
            if (!data) {
                setLoading(false);
                setStatus("error");
                setErrorMessage("Unable to load campus programs");
                return;
            }
            setSetName(data.setName);
            setEduLevels(data.eduLevels || []);
            setPrograms(data.programs || []);
            const map: Record<CellKey, string> = {};
            (data.cells || []).forEach((c: any) => {
                map[cellKey(c.programId, c.eduLevelId)] = c.value || "";
            });
            setInitialValues(map);
            setLoading(false);
            setStatus("idle");
        };
        load().then(() => {});
    }, [programSetId, campusName]);

    const handleSaving = React.useCallback((delta: number) => {
        setPendingCount((c) => {
            const next = Math.max(0, c + delta);
            if (next > 0) setStatus("saving");
            return next;
        });
        if (delta > 0) {
            setErrorMessage("");
        }
    }, []);

    const handleSaved = React.useCallback(() => {
        setSavedAt(moment().format("h:mm:ss A"));
        setStatus((s) => (s === "error" ? s : "saved"));
    }, []);

    const handleError = React.useCallback((msg: string) => {
        setStatus("error");
        setErrorMessage(msg);
    }, []);

    const statusLabel = () => {
        if (status === "saving" || pendingCount > 0) {
            return pendingCount > 1 ? `Saving… (${pendingCount})` : "Saving…";
        }
        if (status === "saved") return `Saved at ${savedAt}`;
        if (status === "error") return errorMessage || "Error";
        return "";
    };

    if (!programSetId || !campusName) {
        return <Alert severity="error">Missing program set id or campus name</Alert>;
    }

    return (
        <>
            <Box sx={{mb: 1}}>
                <Typography variant="h5" component="h2" sx={{fontWeight: "bold", textAlign: "center"}}>
                    Program Set Program Update
                </Typography>
                <Typography sx={{textAlign: "center"}}>
                    Program Set <b>{setName || programSetId}</b> — campus <b>{campusName} [{programSetId}]</b>
                </Typography>
                <Box sx={{textAlign: "center"}}>
                    <Link component={RouterLink} to={`/programSets/${programSetId}`}>Back</Link>
                </Box>
                <Typography sx={{fontWeight: "bold", color: "primary.main", mt: 1, fontSize: 13}}>
                    Education Levels below are the highest level the lead already has, not what they are looking for.
                </Typography>
                <Typography
                    sx={{
                        mt: 0.5,
                        fontSize: 13,
                        fontWeight: "bold",
                        color: status === "error" ? "error.main" : status === "saved" ? "success.main" : "text.secondary",
                        minHeight: 20,
                    }}
                >
                    {statusLabel()}
                </Typography>
            </Box>

            {loading ? <Loading/> :
                <ProgramGrid
                    programSetId={programSetId}
                    campusName={campusName}
                    eduLevels={eduLevels}
                    programs={programs}
                    initialValues={initialValues}
                    onSaving={handleSaving}
                    onSaved={handleSaved}
                    onError={handleError}
                />
            }
        </>
    );
}
